import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useStyles } from './CloudAccountContent.styles'
import { faTrash } from '@fortawesome/pro-regular-svg-icons'
import {
  BuyerDetailRow,
  BuyerInfo,
} from '../../common/components/DetailRow/DetailRow'
import {
  CardsContainer,
  ColumnActionWithMenuV2,
  ColumnMenuOption,
  ModalConfirmV2,
  ModalSizeVariants,
  OfferCard,
} from '@labrav/react-components'
import { useDispatch, useSelector } from '../../store'
import { useNavigate, useParams } from 'react-router-dom'
import { useState } from 'react'
import type { AccountDataSelection } from '@labrav/common'
import { deleteAccountAction } from './accountSelectionActions'
import {
  formatAccountCurrencies,
  getCloudLogoSrc,
  isFlyoutAccount,
} from './helpers'

const FlyoutCloudAccountContent: React.FC = () => {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { partnerId, secureKey: secureKeyFromParams } = useParams<{
    partnerId?: string
    secureKey?: string
  }>()

  const secureKey = secureKeyFromParams || window.LABRA_JWT
  const [openRemoveModal, setOpenRemoveModal] = useState(false)

  const [selectedAccount, setSelectedAccount] =
    useState<null | AccountDataSelection>(null)

  const accountsData = useSelector(
    state =>
      (state.accountSelection?.availableAccounts as
        | AccountDataSelection[]
        | undefined) || []
  )
  const isDeleting = useSelector(state => state.accountSelection?.isLoading)

  const marketplaceAccountsData = accountsData.filter(isFlyoutAccount)

  const handleCloseRemoveModal = () => {
    setOpenRemoveModal(false)
    setSelectedAccount(null)
  }

  const handleAccountSelect = (account: AccountDataSelection) => {
    const cloudType = account.cloud.toLowerCase()
    const accountId = account.id
    if (secureKey && partnerId) {
      navigate(
        `/external-apps/${secureKeyFromParams ? `${secureKeyFromParams}/` : ''}partners/${partnerId}/settings/flyout/cloud/${cloudType}/${accountId}`
      )
    } else {
      navigate(`/settings/flyout/cloud/${cloudType}/${accountId}`)
    }
  }

  const handleActionsSelectOption = async (
    option: ColumnMenuOption,
    account: AccountDataSelection
  ) => {
    if (option.key === 'remove-cloud-account') {
      setSelectedAccount(account)
      setOpenRemoveModal(true)
    }
  }

  const handleRemoveAccount = async () => {
    if (selectedAccount) {
      await dispatch(
        deleteAccountAction(
          selectedAccount.id,
          selectedAccount.cloud,
          selectedAccount.product
        )
      )
      handleCloseRemoveModal()
    }
  }

  const getColumnActionWithMenuOptions = (account: AccountDataSelection) => [
    {
      key: 'remove-cloud-account',
      label: 'Remove cloud account',
      onClick: () => {
        setSelectedAccount(account)
        setOpenRemoveModal(true)
      },
      disable: marketplaceAccountsData.length <= 1,
      disabledTooltip: 'You cannot remove the last cloud account.',
    },
  ]

  return (
    <div
      data-testid="marketplace-cloud-account-content"
      className={classes.rootContainer}
    >
      <div className={classes.leftContainer}>
        {marketplaceAccountsData?.map((account, index) => (
          <CardsContainer
            key={account.id || index}
            hoverEffect={true}
            handleClick={() => handleAccountSelect(account)}
          >
            <OfferCard
              name={
                <div className={classes.accountNameContainer}>
                  <div className={classes.accountName}>
                    {account.name || '-'}
                  </div>{' '}
                </div>
              }
              isLastCard={true}
              customiseForBuyer={true}
              rightSideSection={
                <>
                  <div onClick={e => e.stopPropagation()}>
                    <ColumnActionWithMenuV2
                      handleSelectOption={option =>
                        handleActionsSelectOption(option, account)
                      }
                      menuOptions={getColumnActionWithMenuOptions(account)}
                    />
                  </div>
                </>
              }
              hideLogo
            />
            <BuyerDetailRow>
              <>
                <BuyerInfo
                  title={'Cloud account ID'}
                  value={
                    <div className={classes.cloudAccountIdContainer}>
                      <img
                        src={getCloudLogoSrc(account.cloud)}
                        alt="Cloud Icon"
                        className={classes.cloudIcon}
                      />
                      <div className={classes.cloudAccountId}>
                        {account.cloud === 'AZURE'
                          ? account?.metadata?.mpn_id
                          : account.externalAccountId || '-'}
                      </div>
                    </div>
                  }
                  gridSize={6}
                />
                <BuyerInfo
                  title={'Currencies'}
                  value={
                    <div className={classes.cloudAccountId}>
                      {formatAccountCurrencies(account.currencies)}
                    </div>
                  }
                  showDivider={false}
                  gridSize={6}
                />
              </>
            </BuyerDetailRow>
          </CardsContainer>
        ))}
      </div>
      <ModalConfirmV2
        title={`Remove ${selectedAccount?.name}`}
        icon={
          <FontAwesomeIcon
            icon={faTrash}
            className={classes.trashIcon}
            data-testid="remove-cloud-account-modal-icon"
          />
        }
        onlyCloseModal
        onCloseModal={handleCloseRemoveModal}
        onDecline={handleCloseRemoveModal}
        content={
          <div className={classes.removeModalContentContainer}>
            <div className={classes.removeModalHeading}>
              Are you sure you want to remove the “{selectedAccount?.name}”
              cloud account?
            </div>
            <div className={classes.removeModalHeading}>
              This action cannot be undone.
            </div>
          </div>
        }
        open={openRemoveModal}
        titleCancelButton={'Cancel'}
        titleAcceptButton={'Remove'}
        onAccept={() => handleRemoveAccount()}
        variant={ModalSizeVariants.medium}
        hideCancelButton={false}
        acceptDisabled={isDeleting}
        acceptLoading={isDeleting}
      />
    </div>
  )
}

export default FlyoutCloudAccountContent
